<script lang="ts" setup>
import { i18n } from '@/i18n';
import { onMounted, onUnmounted, ref, watch } from 'vue';

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'auto' | 'max-content';

// props
const props = withDefaults(
  defineProps<{
    /**
     * モーダルのopen/close管理フラグ
     */
    modalState: boolean;

    /**
     * モーダルのタイトル
     */
    modalTitle?: string;

    /**
     * モーダルのサイズ
     */
    modalSize?: ModalSize | number;

    /**
     * モーダルの幅(px指定)
     */
    modalWidth?: number;

    /**
     * アスペクト比(高さを width の 3/4 にする)
     */
    aspectRatio?: boolean;

    /**
     * 背景のスクロール可否管理フラグ
     */
    scrollOff?: boolean;

    /**
     * モーダルの中身に付けるpadding
     */
    padding?: string;

    /**
     * モーダル外側クリックで閉じるかどうか
     */
    mdClickOutSideToClose?: boolean;

    /**
     * モーダルの内部コンテナを最大化するかどうか
     */
    stretchContainer?: boolean;

    /**
     * 余白系をゼロにするかどうか
     */
    noPaddingAndMargin?: boolean;

    /**
     * モーダルの角丸
     */
    borderRadius?: string;

    /**
     * backdropに色を付けるかどうか
     */
    fillBackdrop?: boolean;

    /**
     * ESCキーによる閉じ処理を禁止するかどうか
     */
    noCancel?: boolean;

    /**
     * モーダル本体にoverflow: hiddenをつけるかどうか
     */
    isModalOverflowHidden?: boolean;
  }>(),
  {
    modalTitle: '',
    modalSize: 'md',
    modalWidth: undefined,
    aspectRatio: false,
    scrollOff: false,
    padding: '30px',
    mdClickOutSideToClose: true,
    stretchContainer: false,
    noPaddingAndMargin: false,
    borderRadius: '0',
    fillBackdrop: true,
    noCancel: false,
    isModalOverflowHidden: false,
  }
);

// emits
const emits = defineEmits<{
  (e: 'outside-click', status: boolean): void;
}>();

/**
 * モーダル幅を取得する
 * @param size - モーダルサイズ
 * @returns 設定する幅(px or auto/max-content)
 */
const getWidth = (size: ModalSize | number) => {
  if (props.modalWidth) {
    if (props.modalSize === 'auto' || props.modalSize === 'max-content') {
      return props.modalWidth + 'px';
    } else {
      return props.modalWidth;
    }
  }

  if (!size) {
    return 720;
  } else if (typeof size === 'string') {
    switch (size) {
      case 'xs':
        return 300;
      case 'sm':
        return 540;
      case 'md':
        return 720;
      case 'lg':
        return 900;
      case 'xl':
        return 1080;
      case '2xl':
        return 1200;
      case '3xl':
        return 1320;
      case 'auto':
        return 'auto';
      case 'max-content':
        return 'max-content';
    }
  } else {
    return size;
  }
};

/**
 * モーダル高さを取得する
 * @param size - モーダルサイズ
 * @returns 設定する高さ(パーセンテージ or auto/max-content)
 */
const getHeight = (size: ModalSize | number): string => {
  if (!size) {
    return `${720 * 0.75}px`;
  } else if (typeof size === 'string') {
    switch (size) {
      case 'xs':
        return '40%';
      case 'sm':
        return '40%';
      case 'md':
        return '50%';
      case 'lg':
        return '60%';
      case 'xl':
        return '70%';
      case '2xl':
        return '80%';
      case '3xl':
        return '80%';
      case 'auto':
        return 'auto';
      case 'max-content':
        return 'max-content';
    }
  } else {
    return `${size * 0.75}px`;
  }
};

// 変数
const modalRef = ref<HTMLDivElement>(); // モーダルのref
const t = i18n.t;

/**
 * モーダルを表示するときの処理
 */
const openModal = () => {
  document.body.style.overflow = 'hidden'; // 背景スクロールを抑制
  // ESCキーの制御 (必要であれば)
  if (props.noCancel) {
    window.addEventListener('keydown', preventEsc);
  }
};

/**
 * モーダルを閉じるときの処理
 */
const closeModal = () => {
  document.body.style.overflow = ''; // 背景スクロールを戻す
  // ESCキーの制御を解除
  if (props.noCancel) {
    window.removeEventListener('keydown', preventEsc);
  }
};

/**
 * 外側クリックで閉じるときのハンドラ
 * @param e - クリックイベント
 */
const onClickModal = (e: MouseEvent) => {
  // クリックした要素がモーダルのラッパ要素そのものであれば閉じる
  if (e.target === modalRef.value && props.mdClickOutSideToClose) {
    emits('outside-click', false);
  }
};

/**
 * ESCキー押下を無効にする
 * @param e - キーボードイベント
 */
function preventEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
  }
}

// 親コンポーネントから渡ってくる modalState の変化を監視
watch(
  () => props.modalState,
  (newVal) => {
    if (newVal) {
      openModal();
    } else {
      closeModal();
    }
  }
);

// 初期表示時の対応
onMounted(() => {
  if (props.modalState) {
    openModal();
  }
});

onUnmounted(() => {
  if (props.modalState) {
    closeModal();
  }
});
</script>

<template>
  <!-- v-if でモーダルの表示・非表示を切り替える -->
  <div
    v-if="modalState"
    ref="modalRef"
    class="modal-content"
    :class="{ 'modal-content--fill-backdrop': fillBackdrop }"
    @click="onClickModal"
  >
    <!-- 実際のモーダル本体部分 -->
    <div class="modal-wrapper modal-content__modal-wrapper">
      <div class="content-container modal-wrapper__content-container">
        <div v-if="modalTitle !== ''" class="title content-container__title">
          <span class="title__title-text">{{ t(modalTitle) }}</span>
        </div>
        <div class="content-container__content-wrapper">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  /* モーダル背景 (オーバーレイ) */
  position: fixed;
  inset: 0;
  z-index: 21;

  &--fill-backdrop {
    background: rgba(0, 0, 0, 0.5);
    transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.modal-wrapper {
  /* 以下のサイズ計算に、Composition API の関数をそのまま使えないため、
     実際には style バインドなどで制御します。ここでは擬似的に示すために
     カスタムプロパティやインラインスタイルを使用するイメージで記述しています。 */

  position: absolute;
  inset: 0;
  margin: auto;
  z-index: 21;

  /* v-bindで算出した幅や高さを設定するイメージ */
  width: v-bind(
    'props.modalSize === `auto` || props.modalSize === `max-content` ? getWidth(props.modalSize) : getWidth(props.modalSize) + `px`'
  );
  height: v-bind('props.aspectRatio ? getHeight(props.modalSize) : `fit-content`');
  max-width: 100%;
  max-height: 100%;
  overflow: v-bind('props.scrollOff ? props.isModalOverflowHidden ? `hidden` : `` : `scroll`');
  background-color: $semanticWhite;
  border-radius: v-bind('props.borderRadius');

  /* スクロールバーを非表示 */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE, Edge */

  &__content-container {
    padding: v-bind('props.noPaddingAndMargin ? `0.4em 2em` : `0`');
  }
}

.content-container {
  /* stretchContainer が有効な場合は100%に */
  height: v-bind('props.stretchContainer ? `100%` : `auto`');
  background-color: var(--vp-c-bg, $semanticWhite);
  border-radius: 5px;
  border: 1px solid var(--vp-c-divider, $gray9);
  box-shadow: 2px 2px 10px rgba(10, 10, 10, 0.1);

  &__title {
    padding: 20px 0px 15px 30px;
    display: flex;
    justify-content: center;
  }

  &__content-wrapper {
    height: v-bind('props.stretchContainer ? `100%` : `auto`');
    padding: v-bind('props.padding');
  }
}

.title {
  &__title-text {
    font-family: Inter, sans-serif;
    font-size: 1.125rem; /* 任意の大きさ */
    font-weight: 700;
  }
}
</style>
